import { useMutation, useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

// SaaS subscription billing (Roadmap Phase 2 Payments). Read the org's
// subscription state + the platform plan catalogue, and start a Stripe Checkout /
// Billing Portal session through the stripe-create-subscription-checkout edge
// function. Mirrors useInvoices / useCreateCheckoutSession conventions.

// The auto-generated Supabase types don't yet include the subscription tables.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const db = supabase as any;

export type SubscriptionPlan = 'starter' | 'pro' | 'business';
export type BillingInterval = 'month' | 'year';
export type SubscriptionStatus =
  | 'trialing'
  | 'active'
  | 'past_due'
  | 'canceled'
  | 'incomplete';

export interface OrgSubscription {
  id: string;
  organization_id: string;
  plan: SubscriptionPlan;
  status: SubscriptionStatus;
  trial_ends_at: string | null;
  current_period_start: string | null;
  current_period_end: string | null;
  cancel_at_period_end: boolean;
  canceled_at: string | null;
  stripe_customer_id: string | null;
  stripe_subscription_id: string | null;
  stripe_price_id: string | null;
  stripe_status: string | null;
}

export interface SubscriptionPrice {
  id: string;
  plan: SubscriptionPlan;
  stripe_price_id: string;
  billing_interval: BillingInterval;
  unit_amount: number;
  currency_code: string;
  trial_period_days: number;
  active: boolean;
}

/** The active org's subscription row (one per tenant). */
export function useOrgSubscription(orgId: string | null | undefined) {
  return useQuery({
    queryKey: ['org-subscription', orgId],
    enabled: Boolean(orgId),
    queryFn: async (): Promise<OrgSubscription | null> => {
      const { data, error } = await db
        .from('org_subscriptions')
        .select(
          'id, organization_id, plan, status, trial_ends_at, current_period_start, current_period_end, cancel_at_period_end, canceled_at, stripe_customer_id, stripe_subscription_id, stripe_price_id, stripe_status',
        )
        .eq('organization_id', orgId)
        .maybeSingle();
      if (error) throw error;
      return (data ?? null) as OrgSubscription | null;
    },
  });
}

/**
 * The billing fields every member of the org may see (via the
 * org_billing_access RPC), used to decide whether the admin is locked.
 */
export function useOrgBillingAccess(orgId: string | null | undefined) {
  return useQuery({
    queryKey: ['org-billing-access', orgId],
    enabled: Boolean(orgId),
    staleTime: 60_000,
    queryFn: async () => {
      const { data, error } = await db.rpc('org_billing_access', { _org_id: orgId });
      if (error) throw error;
      const row = (Array.isArray(data) ? data[0] : data) as
        | { status: string; trial_ends_at: string | null; stripe_status: string | null; has_stripe_subscription: boolean }
        | undefined;
      if (!row) return null;
      return {
        status: row.status,
        trial_ends_at: row.trial_ends_at,
        stripe_status: row.stripe_status,
        // The gate only needs to know a Stripe subscription exists.
        stripe_subscription_id: row.has_stripe_subscription ? 'present' : null,
      };
    },
  });
}

/** The platform's active plan catalogue, cheapest first. */
export function useSubscriptionPrices() {
  return useQuery({
    queryKey: ['subscription-prices'],
    queryFn: async (): Promise<SubscriptionPrice[]> => {
      const { data, error } = await db
        .from('subscription_prices')
        .select('id, plan, stripe_price_id, billing_interval, unit_amount, currency_code, trial_period_days, active')
        .eq('active', true)
        .order('unit_amount', { ascending: true });
      if (error) throw error;
      return (data ?? []) as SubscriptionPrice[];
    },
  });
}

export interface SubscriptionCheckoutInput {
  organizationId: string;
  plan?: SubscriptionPlan;
  interval?: BillingInterval;
  mode: 'checkout' | 'portal';
}

export interface SubscriptionCheckoutResult {
  configured: boolean;
  mode?: 'checkout' | 'portal';
  url?: string;
  sessionId?: string;
}

/**
 * Start a plan Checkout (mode: 'checkout') or open the Billing Portal
 * (mode: 'portal') via the edge function. Returns { configured: false } when
 * Stripe / the price catalogue isn't set up server-side, so the caller can fail
 * safe rather than redirect.
 */
export function useSubscriptionCheckout() {
  return useMutation({
    mutationFn: async (input: SubscriptionCheckoutInput): Promise<SubscriptionCheckoutResult> => {
      const { data, error } = await supabase.functions.invoke('stripe-create-subscription-checkout', {
        body: {
          organizationId: input.organizationId,
          plan: input.plan,
          interval: input.interval ?? 'month',
          mode: input.mode,
        },
      });
      if (error) throw error;
      return (data ?? {}) as SubscriptionCheckoutResult;
    },
  });
}
